import React, { useMemo } from 'react'
import { Box, Collapse, InputLabel, Stack } from '@mui/material'

import { SeedMix, SeedMixSchema } from '../types/SeedOrderFormType'
import TestIds from '../../../testIds'
import Typography from '../../../components/Typography'
import Input from '../../../components/Input'
import { ExpandIcon } from '../components/ExpandIcon'
import { QuantityChip } from '../../dashboard/components/QuantityChip'
import { useSelector } from 'react-redux'
import { selectCurrentFarmerAccount } from '../../azure-b2c/storage'
import { colors } from '../../../themes/colors'
import { useAppDispatch } from '../../../store/rootStore'
import { setSeedValue } from '../storage/seedCart.slice'

export const NotesForm = ({ seedMix }: { seedMix: SeedMix }) => {
  const account = useSelector(selectCurrentFarmerAccount)
  const isTfo = account?.hasTfoAccess || false

  const dispatch = useAppDispatch()

  const showNotes = seedMix.notesExpanded
  const internalNoteField = seedMix.internalNotes
  const externalNoteField = seedMix.externalNotes

  const validation = SeedMixSchema.safeParse(seedMix)
  const internalNotesError = validation.error?.formErrors?.fieldErrors?.internalNotes?.[0]
  const externalNotesError = validation.error?.formErrors?.fieldErrors?.externalNotes?.[0]

  const setShowNotes = (show: boolean) => {
    const accountNumber = account?.accountNumber
    if (accountNumber) {
      dispatch(
        setSeedValue({
          accountNumber,
          key: `notesExpanded`,
          value: show,
          seedMix,
        }),
      )
    }
  }

  const setInternalNotes = (value: string) => {
    const accountNumber = account?.accountNumber
    if (accountNumber) {
      dispatch(
        setSeedValue({
          accountNumber,
          key: `internalNotes`,
          value: value,
          seedMix,
        }),
      )
    }
  }

  const setExternalNotes = (value: string) => {
    const accountNumber = account?.accountNumber
    if (accountNumber) {
      dispatch(
        setSeedValue({
          accountNumber,
          key: `externalNotes`,
          value: value,
          seedMix,
        }),
      )
    }
  }

  const numOfNotes = useMemo(() => {
    let count = 0
    if (internalNoteField) count++
    if (externalNoteField) count++
    switch (count) {
      case 1:
        return '1 note'
      case 2:
        return '2 notes'
      default:
        return 'No notes'
    }
  }, [internalNoteField, externalNoteField])

  return (
    <>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        paddingX='16px'
        paddingY='24px'
      >
        <Stack direction='row' alignItems='center' spacing={1}>
          <Typography variant='h6' fontWeight='700' fontSize='20px'>
            Notes
          </Typography>
          <QuantityChip label={numOfNotes} />
        </Stack>

        <Stack direction='row' alignItems='center' spacing={1}>
          {showNotes ? (
            <ExpandIcon icon='expand_less' onClick={() => setShowNotes(false)} />
          ) : (
            <ExpandIcon icon='expand_more' onClick={() => setShowNotes(true)} />
          )}
        </Stack>
      </Stack>

      <Collapse in={showNotes}>
        <Box paddingX='16px'>
          {isTfo ? (
            <Input
              label='Internal Farmlands: (Optional)'
              errorMessage={internalNotesError}
              type='text'
              multiline
              minRows={4}
              value={internalNoteField}
              helperText={`${internalNoteField ? internalNoteField.length : 0}/200 characters`}
              testId={TestIds.feat.seed.note.internalNote}
              inputProps={{
                maxLength: 200,
              }}
              setValue={(value) => {
                setInternalNotes(value as string)
              }}
            />
          ) : null}
          <Input
            label='External: (Optional)'
            errorMessage={externalNotesError}
            type='text'
            multiline
            minRows={4}
            value={externalNoteField}
            helperText={`${externalNoteField ? externalNoteField.length : 0}/200 characters`}
            testId={TestIds.feat.seed.note.externalNote}
            inputProps={{
              maxLength: 200,
            }}
            setValue={(value) => {
              setExternalNotes(value as string)
            }}
          />
        </Box>
      </Collapse>
    </>
  )
}

export const NotesFormReadOnly = ({
  internalNotes,
  externalNotes,
}: {
  internalNotes?: string
  externalNotes?: string
}) => {
  const account = useSelector(selectCurrentFarmerAccount)
  const isTfo = account?.hasTfoAccess || false

  const numOfNotes = useMemo(() => {
    let count = 0
    if (internalNotes) count++
    if (externalNotes) count++
    switch (count) {
      case 1:
        return '1 note'
      case 2:
        return '2 notes'
      default:
        return 'No notes'
    }
  }, [internalNotes, externalNotes])

  if (!internalNotes && !externalNotes) return null
  return (
    <Stack
      direction='column'
      spacing={1}
      bgcolor={colors.shade0}
      px={2}
      py={3}
      borderBottom={`1px solid ${colors.primary100}`}
    >
      <Stack direction='row' alignItems='flex-start' gap={1} pb={3}>
        <Typography variant='h6' fontWeight='700' fontSize='20px'>
          Notes
        </Typography>
        <QuantityChip label={numOfNotes} />
      </Stack>
      <Stack gap={1}>
        {internalNotes && isTfo && (
          <Stack>
            <InputLabel>Internal Farmlands:</InputLabel>
            <Typography variant='body2'>{internalNotes}</Typography>
          </Stack>
        )}
        {externalNotes && (
          <Stack>
            <InputLabel>External:</InputLabel>
            <Typography variant='body2'>{externalNotes}</Typography>
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}
